import { FC, memo, useMemo } from "react";
import { getTeamColors } from "components/colors";
import { MatchStatus } from "service";
import { Text } from "components/common/Text";
import { TwoTeamScoreBoard } from "../../../components/common";

const getPair = (value: unknown): [number, number] | null => {
  if (Array.isArray(value) && value.length >= 2) {
    const home = Number(value[0]);
    const away = Number(value[1]);
    if (!Number.isFinite(home) && !Number.isFinite(away)) return null;
    return [Number.isFinite(home) ? home : 0, Number.isFinite(away) ? away : 0];
  }

  if (typeof value === "string" && value.includes(":")) {
    const [homeRaw, awayRaw] = value.split(":");
    const home = Number(homeRaw);
    const away = Number(awayRaw);
    if (!Number.isFinite(home) && !Number.isFinite(away)) return null;
    return [Number.isFinite(home) ? home : 0, Number.isFinite(away) ? away : 0];
  }

  return null;
};

const sumInnings = (c: Record<string, unknown> | undefined) => {
  if (!c) return null;
  const innings = Object.entries(c)
    .filter(([key]) => /^I\d+$/i.test(key))
    .map(([, value]) => getPair(value))
    .filter((pair): pair is [number, number] => !!pair);

  if (innings.length === 0) return null;

  return innings.reduce<[number, number]>(
    (acc, [home, away]) => [acc[0] + home, acc[1] + away],
    [0, 0],
  );
};

const humanizeLabel = (raw?: string | null) => {
  if (!raw || typeof raw !== "string") return undefined;
  const normalized = raw
    .replace(/[_^]/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([0-9])([A-Za-z])/g, "$1 $2")
    .replace(/([A-Za-z])([0-9])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();

  if (!normalized) return undefined;

  const withOfSpacing = normalized.replace(
    /\b([A-Za-z]+)of\b/gi,
    (_, prefix) => {
      return `${prefix} of`;
    },
  );

  return withOfSpacing.toUpperCase();
};

const parseInningLabel = (sts?: string | null, stn?: string | null) => {
  if (typeof sts === "string" && sts.length > 0) {
    const parts = sts
      .split("|")
      .map((part) => part.trim())
      .filter(Boolean);

    const candidate = parts.find((part) =>
      /(top|bottom|mid|middle|end)/i.test(part),
    );

    if (candidate) {
      return humanizeLabel(candidate);
    }
  }

  return humanizeLabel(stn);
};

const parseCounts = (sts?: string | null) => {
  const result: Record<string, number> = {};
  if (!sts || typeof sts !== "string") return result;

  const parts = sts
    .split("|")
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length > 0) {
    const outsCandidate = Number(parts[0]);
    if (Number.isFinite(outsCandidate) && outsCandidate >= 0) {
      result.Outs = outsCandidate;
    }
  }

  for (let i = 0; i < parts.length; i += 1) {
    const segment = parts[i];
    if (segment.startsWith("STS=")) {
      const keys = segment
        .slice(4)
        .split(",")
        .map((key) => key.trim());

      const valuesPart = parts[i + 1] ?? "";
      const valuesString = valuesPart.startsWith("^")
        ? valuesPart.slice(1)
        : valuesPart;
      const values = valuesString.split(",");

      keys.forEach((key, index) => {
        const value = Number(values[index]);
        if (Number.isFinite(value)) {
          result[key] = value;
        }
      });
      break;
    }
  }

  return result;
};

export const Timer: FC<{ status: MatchStatus }> = memo(({ status }) => {
  const { nsc, c, tm, sts, stn, ...rest } = status as any;

  const scores = useMemo<[number, number]>(() => {
    const totals =
      getPair(c?.R) ??
      getPair(c?.T) ??
      getPair(c?.Score) ??
      getPair(c?.SCORE) ??
      sumInnings(c);

    return totals ?? [0, 0];
  }, [c]);

  const inningLabel = useMemo(() => parseInningLabel(sts, stn), [sts, stn]);

  const rawCounts = useMemo(() => parseCounts(sts), [sts]);

  const counts = useMemo(() => {
    const entries: { label: string; value: number }[] = [];

    const outs = rawCounts.Outs;
    if (
      typeof outs === "number" &&
      Number.isFinite(outs) &&
      outs >= 0 &&
      outs <= 3
    ) {
      entries.push({ label: "OUTS", value: outs });
    }

    const balls = rawCounts.Balls;
    if (
      typeof balls === "number" &&
      Number.isFinite(balls) &&
      balls >= 0 &&
      balls <= 4
    ) {
      entries.push({ label: "BALLS", value: balls });
    }

    const strikes = rawCounts.Strikes;
    if (
      typeof strikes === "number" &&
      Number.isFinite(strikes) &&
      strikes >= 0 &&
      strikes <= 3
    ) {
      entries.push({ label: "STRIKES", value: strikes });
    }

    return entries;
  }, [rawCounts]);

  const { home: homeColor, away: awayColor } = getTeamColors(rest);

  return (
    <div className="absolute left-1/2 top-0 flex -translate-x-1/2 flex-col items-center justify-center gap-[0.4vw] pt-[0.5vw]">
      <TwoTeamScoreBoard
        className="h-[6vw]"
        width="14vw"
        scoreHeight="6vw"
        accentHeight="1vw"
        gap="0.25vw"
        columnGap="0.2vw"
        homeAccentColor={homeColor}
        awayAccentColor={awayColor}
        homeContent={scores[0]}
        awayContent={scores[1]}
      />

      {inningLabel && (
        <div className="flex flex-row items-center justify-center gap-[0.4vw]">
          <div className="rounded-[0.3vw] bg-[rgba(13,17,32,0.85)] px-[0.8vw] py-[0.4vw] font-bold tracking-[0.05em] text-white">
            <Text variant="scoreboardBaseballInning">{inningLabel}</Text>
          </div>
          {counts.length > 0 && (
            <div className="flex items-center justify-center gap-[0.4vw]">
              {counts.map((item) => (
                <div
                  key={item.label}
                  className="flex min-w-[3.4vw] flex-col items-center justify-center rounded-[0.35vw] bg-white/85 px-[0.7vw] py-[0.35vw] text-center text-[#0b1729]"
                >
                  <Text
                    variant="scoreboardBaseballCounts"
                    className="flex flex-col items-center"
                  >
                    <strong className="tracking-[0.04em]">{item.label}</strong>
                    <span className="mt-[0.1vw] font-bold">{item.value}</span>
                  </Text>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
});
